import React, { useState, useRef } from "react";
import "./contentpage.css";
import Question from "../components/Question";
import FloatingPageNav from "../components/FloatingPageNav";
import LandingSection from "./LandingSection";

function Economics29() {

   const contentRef = useRef(null);
       const links = {
      prev: "/economics-28", 
      index: "/index-economics",                 
      next: "/economics-30", 
    };
    const questions = [
  {
    text: "1. What does marginalism primarily focus on in economic decision-making?",
    options: [
      {
        value: "A",
        label: "A. The total value of goods and services",
      },
      {
        value: "B",
        label: "B. The average cost of production",
      },
      {
        value: "C",
        label:
          "C. The additional benefit or utility of the next unit consumed or produced",
      },
      {
        value: "D",
        label: "D. The total labor input required for a product",
      },
    ],
    correct: "C",
  },
  {
    text: "2. Which economic puzzle does marginalism help to resolve?",
    options: [
      {
        value: "A",
        label: "A. Why inflation occurs during war",
      },
      {
        value: "B",
        label: "B. Why barter systems are inefficient",
      },
      {
        value: "C",
        label:
          "C. Why water is cheaper than diamonds despite being more essential",
      },
      {
        value: "D",
        label: "D. Why governments impose taxes",
      },
    ],
    correct: "C",
  },
  {
    text: "3. According to marginalism, why does the value of a good decline as one consumes more of it?",
    options: [
      {
        value: "A",
        label: "A. Because the good becomes objectively less useful",
      },
      {
        value: "B",
        label: "B. Because prices are fixed over time",
      },
      {
        value: "C",
        label: "C. Due to diminishing marginal utility",
      },
      {
        value: "D",
        label: "D. Because labor value decreases",
      },
    ],
    correct: "C",
  },
  {
    text: "4. What role does marginal utility play in pricing decisions?",
    options: [
      {
        value: "A",
        label: "A. It sets a universal value for each product",
      },
      {
        value: "B",
        label:
          "B. It explains how sellers determine prices based on past production costs",
      },
      {
        value: "C",
        label:
          "C. It influences how much buyers are willing to pay for one additional unit",
      },
      {
        value: "D",
        label: "D. It only applies to luxury goods",
      },
    ],
    correct: "C",
  },
];

      const titleText = "Marginalism: The Core of Economic-Decision Making"
   // State to track selected answers for all questions
      const [answers, setAnswers] = useState({});
    
      // Function to update an answer
      const handleAnswerChange = (questionIndex, selectedValue) => {
        setAnswers((prev) => ({ ...prev, [questionIndex]: selectedValue }));
      };
    
      // Compute score
      const score = questions.reduce((total, q, index) => {
        return total + (answers[index] === q.correct ? 1 : 0);
      }, 0);

  return (
    <div className="container">
      <LandingSection
              titleText={titleText}
              scrollToRef={contentRef}
              progressValue={29}
              timerMinutes={60}
              currentLevel={2}
              unlockedBadges={3}
            />

      <p className="content">
        Welcome to <b>Day 29</b>! Today, we take a significant step forward in
        our exploration of economic reasoning by diving into the concept of
        Marginalism, a cornerstone of modern economics and a vital contribution
        of the Austrian School. Marginalism helps us understand how individuals
        make decisions at the margin, not in terms of total or average values.
        Whether choosing a second cup of tea, buying an extra book, or working
        one more hour, human beings weigh the additional benefit of one more
        unit against the cost of obtaining it. This concept not only clarifies
        how prices form and resources are allocated but also corrects the flaws
        of earlier economic theories that failed to grasp why water can be
        cheaper than diamonds despite being more essential. As we unpack
        marginal utility, you will see how this insight reshapes our view of
        value, choice, and economic coordination in a world of scarcity and
        competing priorities.
        <br /> In the vast field of economic thinking, one concept stands out
        for its ability to explain human choice: marginalism. This idea focuses
        on how decisions are made not based on the total value of a good, but on
        the value of the next, or marginal, unit. It offers a powerful lens for
        analyzing how people prioritize, spend, save, and allocate their limited
        resources. <br />
        <br />
        <b>The Central Insight: Choosing at the Margin</b>
        <br />
        <br />
        Imagine someone considering whether to buy an extra cup of coffee.
        They’ve already had one earlier in the day, and this next cup isn't a
        necessity it’s a small luxury, an extra pleasure. Their decision isn't
        guided by the overall importance of coffee in their life, but by the
        specific value this next cup offers. This is marginal thinking i.e
        choosing based on what the next unit brings. In daily life, people
        rarely think in terms of all-or-nothing choices. Instead, they make
        contextual decisions. Should I buy one more shirt? Should I spend one
        more hour working? Each of these is a marginal choice made based on
        present circumstances and future expectations. <br />
        <br />
        <b>Why Value Is Subjective, Not Intrinsic</b> <br />
        <br />A common misunderstanding in economics is the assumption that
        goods have a fixed, built-in value. However, marginal thinking shows
        that value is not a property of things, but a reflection of how people
        feel about them in specific moments. A glass of water might be nearly
        worthless at a restaurant, but invaluable to someone lost in the desert.
        What this reveals is that context shapes worth. The same object can be
        cherished or ignored, depending entirely on what needs it serves at the
        margin. This understanding replaced earlier ideas that tied value to the
        labor or effort that went into making something. Instead, value now
        emerges as a matter of perspective, preference, and situation. <br />
        <br />
        <b>The Water-Diamond Paradox Revisited</b>
        <br />
        <br />A classic puzzle once baffled economists: why are diamonds, which
        serve no essential function, so expensive, while water, which is necessary for
        life is often cheap or free? The answer lies in marginal thinking. Water
        is usually abundant. People have enough to drink, bathe, cook, and
        clean. When it’s readily available, the next unit of water doesn’t solve
        a life-or-death problem; it might just water a houseplant. The value of
        that extra unit is low. Diamonds, by contrast, are rare. Even the
        smallest unit has a high demand and limited supply, making each marginal
        unit valuable. So prices reflect the usefulness of the next unit, not
        the total importance of the good. This also explains how the price of
        the same good can change dramatically depending on its availability and
        perceived usefulness at a given moment. <br />
        <br />
        <b>The Role of Trade-Offs and Opportunity Cost</b>
        <br />
        <br /> Every decision comes at the expense of another possibility. When
        a person spends money on a book, that same money cannot be spent on
        food, travel, or savings. This is the essence of opportunity cost, the
        next best alternative that must be given up. Marginalism naturally
        incorporates this idea. People act to satisfy their most pressing wants
        first. As their needs are met, they begin fulfilling less urgent
        desires. Each new decision involves ranking priorities and weighing
        costs. The more of something one has, the less urgent the next unit
        becomes, and the more appealing other options grow. This framework
        explains everything from consumer behavior to public budgeting. It shows
        why governments, businesses, and individuals all need to ask: is this
        next unit worth what I’m giving up? <br />
        <br />
        <b>Prices as Signals of Marginal Value</b> <br />
        <br />
        Markets work not by command, but through a decentralized process of
        millions of people making marginal decisions. Prices emerge from this
        activity as signals that communicate how much value people place on
        goods and services at the margin. When a product becomes scarce, its
        marginal value rises, and so does its price. This encourages people to
        conserve and producers to supply more. When a product is abundant, its
        marginal value drops, and prices fall accordingly. These price changes
        are not random. They reflect the shifting needs, preferences, and
        resource availability in real time. They allow societies to coordinate
        activity without requiring any one person or institution to have full
        knowledge or control. <br />
        <br />
        <b>Marginal Thinking and Resource Allocation</b>
        <br />
        <br /> In any environment of limited resources, marginal analysis helps
        determine where efforts and funds should go. Should a company invest in
        research, marketing, or operations? Should a government build more
        schools or roads? Should an individual save or spend their next
        paycheck? These questions are best answered by looking at the value of
        the next dollar spent. If a new project offers a higher return than the
        current one, resources should shift accordingly. But if the returns
        diminish, if the marginal value drops, then it may be time to redirect.
        This mindset helps avoid waste and encourages efficiency. It reminds
        decision-makers that every additional unit of effort, time, or money
        must be justified by what it delivers in return. <br />
        <br />
        <b>Economic Policy and the Marginal Approach</b> <br />
        <br />
        Understanding marginalism also brings clarity to public policy. When
        authorities set prices below market levels or offer subsidies without
        considering long-term effects, they often distort marginal choices. For
        instance, if a subsidy makes gasoline cheaper than it naturally would
        be, people may consume more than necessary, leading to traffic,
        pollution, and misallocated funds. The same applies to rent controls,
        minimum wage laws, and other interventions that ignore marginal signals.
        Policies that respect marginal reasoning, on the other hand, tend to
        promote sustainability, responsible consumption, and better targeting of
        aid. They focus not just on intentions, but on outcomes, what actually
        happens when people respond to incentives. <br />
        <br />
        <b>Everyday Life and Marginal Decisions</b> <br />
        <br />
        Marginalism isn't just about markets and money. It applies to personal
        life in countless ways. Each hour of the day is limited. So is mental
        energy, attention, and emotion. Choosing how to spend these whether on
        work, rest, or socializing involves trade-offs. Even minor decisions
        carry marginal implications. Is that extra hour of sleep worth more than
        a morning jog? Is scrolling social media better than calling a friend?
        Is one more cookie going to improve your mood or make you feel worse
        later? By thinking in terms of marginal benefit versus marginal cost,
        people can make better, more intentional choices across all aspects of
        life. <br />
        <br />
        <b>The Hidden Impact of Marginal Choices</b> <br />
        <br />
        Often, the most powerful effects in life come not from big changes, but
        from small, repeated, marginal decisions. Saving a little more each
        month. Exercising a bit longer. Learning something new in a few spare
        minutes. Over time, the compound result of these marginal choices can be
        transformative. This perspective encourages humility and agency. It
        suggests that change is possible not through sweeping overhauls, but
        through consistently making better marginal choices. It also warns that
        even small missteps, when repeated, can lead to negative outcomes.
        Marginalism reminds us that direction matters more than speed. And that
        the next step is always more important than the last. <br />
        <br />
        <b>Time, Productivity, and the Marginal Hour</b> <br />
        <br />
        In work and productivity, marginal thinking helps people assess how to
        spend their time wisely. One more hour at the office may or may not
        yield enough benefit to outweigh fatigue, missed family time, or reduced
        creativity. Managers evaluating employee performance also rely
        consciously or not on marginal value. A worker’s compensation isn’t
        determined by how hard they’ve worked in the past, but by how much value
        their next hour is expected to produce. Similarly, entrepreneurs must
        decide where to apply their next effort. Should they pursue another
        feature, improve customer service, or refine their branding? Each action
        must be weighed for its marginal impact on success. <br />
        <br />
        <b>Consumption, Enjoyment, and Diminishing Returns</b>
        <br />
        <br></br> Marginalism also explains a common human experience:
        diminishing enjoyment. The first slice of pizza tastes amazing. The
        second is still good. By the third or fourth, excitement fades. The same
        pattern applies to entertainment, luxury, and even social events. This
        doesn’t mean people should avoid repeating pleasures. It simply shows
        that satisfaction decreases as needs are repeatedly fulfilled.
        Recognizing this helps avoid overconsumption and promotes more balanced
        choices. It also explains why variety, moderation, and self-restraint
        often lead to greater overall well-being. <br />
        <br />
        <b>Wealth and the Uneven Value of Money</b>
        <br />
        <br /> For someone living paycheck to paycheck, an extra $100 can make a
        big difference. For a billionaire, it might go unnoticed. This is a
        clear illustration of marginal value. The more money someone has, the
        less each additional dollar means. This principle affects how people
        spend, invest, and respond to risk. It also influences debates about
        taxation, welfare, and philanthropy. Understanding marginal value helps
        frame these discussions around real human experience, not abstract
        numbers. <br />
        Marginalism isn’t just a tool for economists. It’s a mindset. it's a way
        to see the world through trade-offs, priorities, and incremental
        choices. It shifts focus from sweeping ideologies and all-or-nothing
        thinking toward grounded, flexible decision-making. It invites
        individuals to consider not just what they value, but how much they
        value the next unit of it. It encourages honesty about opportunity
        costs, reflection on limited resources, and careful attention to
        context. Most importantly, it helps people stay aware that the next step
        matters. The next action, the next choice, the next moment each carries
        weight. <br />
        <br /> From everyday habits to national economies, marginalism reveals
        the logic behind how things work. It shows why some things are priced
        highly while others are nearly free. It explains how markets coordinate
        vast systems of exchange without central control. It highlights how even
        the most personal choices are influenced by cost, context, and
        alternatives. By viewing life through the lens of marginal choices, we
        become better at navigating scarcity, recognizing trade-offs, and making
        decisions that align with what truly matters. In a world where resources
        are always limited, it is not grand ideals but small, consistent
        marginal decisions that shape our reality.
        <br />
        <br />
        <b> Summary of Today’s Lesson</b>
        <br />
        <br /> In today’s lesson, we explored the essential principle of
        Marginalism, which explains how individuals make economic decisions
        based on the additional, or marginal, utility of consuming or producing
        one more unit of a good or service. We learned that value is not
        determined by the total usefulness of a good, but by its value at the
        margin what the next unit means to the individual in a given context.
        This idea resolves classic economic puzzles, such as why water, though
        vital, is often cheaper than diamonds. We also saw how marginal thinking
        underlies all economic choices, from everyday consumer behavior to
        large-scale production decisions. By focusing on how preferences,
        scarcity, and trade-offs work at the margin, marginalism provides a more
        accurate and dynamic understanding of human action in the marketplace.
        <br />
        <br />
        <b>Coming Up Next </b>
        <br />
        <br /> In our next session, we will examine a widely accepted belief in
        modern economics that taxation, when used for government spending, leads
        to greater productivity and social welfare. Drawing on the Austrian
        School’s perspective, Day 30 will challenge this assumption by exploring
        how taxes distort incentives, misallocate resources, and often crowd out
        private innovation and entrepreneurship. We will investigate the
        difference between what is seen such as public projects and what is
        unseen, such as the lost opportunities and wealth that never materialize
        due to taxation. From public goods to capital formation and
        entrepreneurial risk-taking, we will uncover why the Austrian view holds
        that true productivity thrives not through coercive redistribution but
        through voluntary action and market-driven resource allocation.
      </p>
      <h2>Test your understanding</h2>
      
      
        {questions.map((q, index) => (
      <Question
        key={index}
        questionText={q.text}
        options={q.options}
        correctAnswer={q.correct}
        onAnswer={(value) => handleAnswerChange(index, value)} // pass parent handler
      />
    ))}
    
           <div style={{ marginTop: "20px", fontWeight: "bold", fontSize: "1.2em" }}>
            Score: {score} / {questions.length}
          </div>

     
        
       <FloatingPageNav 
              prevLink={links.prev} 
              indexLink={links.index} 
              nextLink={links.next} 
            />
    </div>
  );
}

export default Economics29;
